// pages/payroll/WeeklyBattaReportPrint.jsx
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_URL = "https://nissi-india.com"; // change if needed

// helpers
function qs(name) {
  return new URLSearchParams(window.location.search).get(name) || "";
}
function s(v) {
  return (v ?? "").toString().trim();
}

// For showing in header (optional)
function formatDDMMYYYYFromISO(iso) {
  const x = s(iso);
  const m = x.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return x;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

const WeeklyBattaReportPrint = () => {
  const finYearId = qs("finYearId") || localStorage.getItem("finYearId") || "";

  // filters from query string (same as list page opened)
  const empCode = qs("empCode");
  const empName = qs("empName");
  const department = qs("department"); // can be "Select"
  const location = qs("location"); // can be "Select"
  const fromDate = qs("fromDate"); // YYYY-MM-DD
  const toDate = qs("toDate"); // YYYY-MM-DD

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ overtimeSum: 0, totalValueSum: 0 });

  // You can also load these from your settings API later.
  const company = useMemo(
    () => ({
      name: "Nissi Infotech",
      line1: "Nissi",
      line2: "CIVIL CONSTRUCTION",
      address: "3/42-12, Nagappa colony , kavundampalayam, Coimbatore - 641030 . Tamil Nadu . India .",
      phone: "Phone No : 9003744644",
      email: "infracomsoft-implementation@nissi", // keep as per your original print
    }),
    []
  );

  const fetchPrintData = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("finYearId missing");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/weekly-batta/print-data`, {
        params: {
          finYearId: Number(finYearId),
          empCode: empCode.trim(),
          empName: empName.trim(),
          department,
          location,
          fromDate,
          toDate,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotals(res.data.totals || { overtimeSum: 0, totalValueSum: 0 });
      } else {
        toast.error(res?.data?.message || "Failed to load print data.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrint = () => window.print();

  return (
    <div className="print-root">
      <ToastContainer position="top-right" autoClose={2200} />

      {/* Screen-only toolbar */}
      <div className="no-print toolbar">
        <button onClick={handlePrint} disabled={loading} className="btn">
          Print
        </button>
        <button onClick={() => window.close()} className="btn secondary">
          Close
        </button>
      </div>

      <div className="page">
        {/* Header box */}
        <div className="header-box">
          <div className="header-grid">
            {/* left logo box */}
            <div className="logo-box">
              {/* Put your logo path here if you have */}
              {/* <img src="/assets/nissi-logo.png" alt="logo" className="logo-img" /> */}
              <div className="logo-placeholder">LOGO</div>
            </div>

            {/* center company */}
            <div className="company">
              <div className="company-name">{company.name}</div>
              <div className="company-sub">{company.line1}</div>
              <div className="company-sub strong">{company.line2}</div>
              <div className="company-address">{company.address}</div>
              <div className="company-address">{company.phone}</div>
            </div>

            {/* right email */}
            <div className="company-right">
              <div className="company-address">{company.email}</div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="title">WEEKLY BATTA REPORT</div>

        {/* Optional filter line (you can remove if you want exact old print) */}
        <div className="filters-line">
          <div>
            <b>From:</b> {formatDDMMYYYYFromISO(fromDate)} &nbsp;&nbsp;
            <b>To:</b> {formatDDMMYYYYFromISO(toDate)}
          </div>
          <div className="filters-right">
            {location && location !== "Select" ? (
              <>
                <b>Branch:</b> {location}
              </>
            ) : null}
          </div>
        </div>

        {/* Table */}
        <table className="report-table">
          <thead>
            <tr>
              <th style={{ width: "6%" }}>S.No</th>
              <th style={{ width: "12%" }}>Employee Code</th>
              <th style={{ width: "20%" }}>Employee Name</th>
              <th style={{ width: "14%" }}>Designation</th>
              <th style={{ width: "10%" }}>Days Worked</th>
              <th style={{ width: "12%" }}>Daily Batta</th>
              <th style={{ width: "12%" }}>Over Time</th>
              <th style={{ width: "14%" }}>Total value</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="no-record">
                  No Records Found
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={`${r.fld_employeecode}-${idx}`}>
                  <td className="center">{idx + 1}</td>
                  <td className="center">{r.fld_employeecode}</td>
                  <td>{r.fld_employeename}</td>
                  <td className="center">{r.fld_desgination}</td>
                  <td className="center">{r.fld_daysworked}</td>
                  <td className="center">{r.fld_battaamount}</td>
                  <td className="center">{r.fld_overtime}</td>
                  <td className="center">{r.fld_total}</td>
                </tr>
              ))
            )}

            {/* Footer total row like screenshot: "Total" in Daily Batta column */}
            {rows.length > 0 && (
              <tr className="total-row">
                <td colSpan={5} />
                <td className="total-label center">
                  <b>Total</b>
                </td>
                <td className="center">
                  <b>{totals.overtimeSum}</b>
                </td>
                <td className="center">
                  <b>{totals.totalValueSum}</b>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Signature area */}
        <div className="sign-row">
          <div className="sign">Prepared By</div>
          <div className="sign center">Checked By</div>
          <div className="sign right">Sr.Accounts Manager</div>
        </div>
      </div>

      {/* Styles */}
      <style>{`
        /* Screen */
        .print-root { background: #fff; color: #000; font-family: "Times New Roman", Times, serif; }
        .toolbar { display: flex; gap: 10px; padding: 12px; }
        .btn { padding: 10px 16px; border-radius: 10px; border: 1px solid #111; background: #111; color: #fff; cursor: pointer; font-weight: 700; }
        .btn.secondary { background: #fff; color: #111; }
        .btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .page {
          width: 980px;
          margin: 0 auto;
          padding: 10px 14px 18px 14px;
        }

        .header-box {
          border: 2px solid #000;
          padding: 10px;
        }
        .header-grid {
          display: grid;
          grid-template-columns: 140px 1fr 240px;
          gap: 10px;
          align-items: center;
        }
        .logo-box {
          border: 1px solid #000;
          height: 90px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .logo-placeholder {
          font-size: 14px;
          font-weight: 700;
        }
        .company { text-align: center; }
        .company-name { font-size: 22px; font-weight: 800; }
        .company-sub { font-size: 14px; margin-top: 2px; }
        .company-sub.strong { font-weight: 800; letter-spacing: 0.4px; }
        .company-address { font-size: 12px; margin-top: 2px; }
        .company-right { text-align: right; font-size: 12px; }

        .title {
          margin: 14px 0 8px 0;
          text-align: center;
          font-size: 20px;
          font-weight: 800;
          letter-spacing: 0.6px;
        }

        .filters-line {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          margin-bottom: 8px;
        }
        .filters-right { text-align: right; }

        .report-table {
          width: 100%;
          border-collapse: collapse;
          border: 2px solid #000;
          font-size: 13px;
        }
        .report-table th, .report-table td {
          border: 1px solid #000;
          padding: 7px 6px;
          vertical-align: middle;
        }
        .report-table th {
          font-weight: 800;
          text-align: center;
        }
        .center { text-align: center; }
        .right { text-align: right; }
        .no-record { padding: 22px; text-align: center; font-weight: 700; }
        .total-row td { height: 42px; }
        .total-label { font-weight: 800; }

        .sign-row {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          margin-top: 55px;
          font-size: 14px;
          font-weight: 700;
        }
        .sign { }
        .sign.center { text-align: center; }
        .sign.right { text-align: right; }

        /* Print */
        @media print {
          .no-print { display: none !important; }
          .page { width: auto; margin: 0; padding: 0; }
          @page { size: A4 landscape; margin: 10mm; }
        }
      `}</style>
    </div>
  );
};

export default WeeklyBattaReportPrint;